import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import useBulkDeleteJobs from "../../data/useBulkDeleteJobs";
import useNotification from "../notifications/useNotifications";
import { DeleteJobOutcome, IJob, JobType } from "../../models/Job";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import isJobArchived from "../../utils/isJobArchived";

interface BulkDeleteJobsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const JOB_TYPE_LABELS: Record<JobType, string> = {
  [JobType.Generic]: "Generic",
  [JobType.Body]: "Body",
  [JobType.Mechanical]: "Mechanical",
};

export default function BulkDeleteJobsModal({ open, setOpen }: BulkDeleteJobsModalProps) {
  const { t } = useTranslation("jobs");
  const { addNotification } = useNotification();
  const { allJobs, bulkRemove } = useBulkDeleteJobs(open);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [failures, setFailures] = useState<DeleteJobOutcome[]>([]);

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedIds(new Set());
      setConfirming(false);
      setFailures([]);
    }
  }, [open]);

  // Oldest first — the point of the bulk view is clearing out jobs that were never finished.
  const jobs = useMemo(() => {
    const data = allJobs.data?.data ?? [];
    return [...data].sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    );
  }, [allJobs.data]);

  // Selected jobs stay pinned to the top and stay visible even when the search
  // term doesn't match them, so a selection can't be silently lost mid-search.
  const { selectedJobs, unselectedJobs } = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    // Status is searchable too, so typing "archived" narrows to the old jobs
    const matchesSearch = (job: IJob) =>
      !term ||
      job.title?.toLowerCase().includes(term) ||
      job.description?.toLowerCase().includes(term) ||
      JOB_TYPE_LABELS[job.type]?.toLowerCase().includes(term) ||
      (isJobArchived(job) ? "archived" : "active").includes(term);

    const selected = jobs.filter((job) => selectedIds.has(job.id));
    const unselected = jobs.filter(
      (job) => !selectedIds.has(job.id) && matchesSearch(job)
    );

    return { selectedJobs: selected, unselectedJobs: unselected };
  }, [jobs, selectedIds, searchTerm]);

  const toggleJob = (jobId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
    setConfirming(false);
  };

  const toggleAllVisible = () => {
    const visibleIds = unselectedJobs.map((job) => job.id);

    setSelectedIds((prev) => {
      // Nothing left unselected in view means the action is "clear", otherwise "select all"
      if (visibleIds.length === 0) return new Set<string>();

      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
    setConfirming(false);
  };

  const handleDelete = () => {
    const jobIds = Array.from(selectedIds);
    if (jobIds.length === 0) return;

    bulkRemove.mutate(
      { jobIds },
      {
        onSuccess: (res) => {
          const failed = res.data?.results?.filter((r) => !r.success) ?? [];
          const deletedCount = res.data?.deletedCount ?? 0;

          setFailures(failed);
          setConfirming(false);
          // Keep only the ones that couldn't be deleted selected, so a retry is one click
          setSelectedIds(new Set(failed.map((f) => f.jobId)));

          addNotification({
            variant: failed.length === 0 ? "success" : "warn",
            primaryText:
              failed.length === 0
                ? t("{{count}} jobs deleted", { count: deletedCount })
                : t("{{count}} jobs deleted, {{failed}} failed", {
                    count: deletedCount,
                    failed: failed.length,
                  }),
          });

          if (failed.length === 0) setOpen(false);
        },
        onError: (err: any) => {
          setConfirming(false);
          addNotification({
            variant: "error",
            primaryText: t("Failed to delete jobs"),
            secondaryText: err?.message,
          });
        },
      }
    );
  };

  const rowClasses = "grid grid-cols-12 gap-2 items-center px-3 py-2 text-sm";

  const renderRow = (job: IJob, isSelected: boolean) => {
    const archived = isJobArchived(job);

    return (
      <label
        key={job.id}
        className={classNames(
          rowClasses,
          "border-b border-gray-100 cursor-pointer",
          isSelected ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"
        )}
      >
        <div className="col-span-1 flex items-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleJob(job.id)}
            className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
        </div>
        <div className="col-span-3 font-medium text-gray-800 truncate" title={job.title}>
          {job.title}
        </div>
        <div className="col-span-2">
          <span
            className={classNames(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
              archived
                ? "bg-gray-100 text-gray-600"
                : "bg-green-100 text-green-800"
            )}
          >
            <span
              className={classNames(
                "h-1.5 w-1.5 rounded-full shrink-0",
                archived ? "bg-gray-400" : "bg-green-500"
              )}
            />
            {archived ? t("Archived") : t("Active")}
          </span>
        </div>
        <div className="col-span-2 text-gray-500">{t(JOB_TYPE_LABELS[job.type])}</div>
        <div className="col-span-2 text-gray-500">
          {t("{{count}} Parts", { count: job.jobPartCategories?.length ?? 0 })}
        </div>
        <div className="col-span-2 text-gray-500">
          {formatDateTime(job.created, DateTime.DATE_MED)}
        </div>
      </label>
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-4xl">
      <div>
        {/* Header — Modal renders its own close button, so leave room for it */}
        <div className="flex items-center gap-3 mb-4 pr-8">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 shrink-0">
            <TrashIcon className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t("Bulk Delete Jobs")}</h2>
            <p className="text-sm text-gray-500">
              {t("Select the jobs to delete. Parts already removed, damaged or missing are left untouched.")}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 border-gray-400 rounded-md sm:text-sm"
            placeholder={t("Search jobs by VRM, description, type or status")}
          />
        </div>

        {allJobs.isLoading ? (
          <LoadingWheel />
        ) : (
          <>
            {/* Column headers */}
            <div
              className={classNames(
                rowClasses,
                "bg-gray-50 border-y border-gray-200 !text-xs font-semibold uppercase tracking-wide text-gray-500"
              )}
            >
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={unselectedJobs.length === 0 && selectedIds.size > 0}
                  onChange={toggleAllVisible}
                  title={t("Select all shown")}
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
              </div>
              <div className="col-span-3">{t("Job")}</div>
              <div className="col-span-2">{t("Status")}</div>
              <div className="col-span-2">{t("Type")}</div>
              <div className="col-span-2">{t("Parts")}</div>
              <div className="col-span-2">{t("Created")}</div>
            </div>

            {/* Scrollable rows — selected pinned above the divider */}
            <div className="max-h-[45vh] overflow-y-auto border-x border-b border-gray-200 rounded-b-md">
              {selectedJobs.map((job) => renderRow(job, true))}

              {selectedJobs.length > 0 && unselectedJobs.length > 0 && (
                <div className="px-3 py-1 bg-gray-50 text-xs font-medium text-gray-400 border-b border-gray-100">
                  {t("{{count}} selected", { count: selectedJobs.length })}
                </div>
              )}

              {unselectedJobs.map((job) => renderRow(job, false))}

              {selectedJobs.length === 0 && unselectedJobs.length === 0 && (
                <p className="px-3 py-6 text-sm text-center text-gray-400 italic">
                  {searchTerm ? t("No jobs match your search") : t("No jobs to show")}
                </p>
              )}
            </div>
          </>
        )}

        {/* Failures from the last attempt */}
        {failures.length > 0 && (
          <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0" />
              <p className="text-sm font-medium text-amber-800">
                {t("{{count}} jobs could not be deleted", { count: failures.length })}
              </p>
            </div>
            <ul className="ml-7 list-disc text-xs text-amber-700">
              {failures.map((failure) => (
                <li key={failure.jobId}>
                  {failure.jobTitle ?? failure.jobId} — {failure.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {t("{{count}} selected", { count: selectedIds.size })}
          </p>

          <div className="flex items-center gap-3">
            {confirming ? (
              <>
                <p className="text-sm font-medium text-red-700">
                  {t("Delete {{count}} jobs? This cannot be undone.", { count: selectedIds.size })}
                </p>
                <ButtonInput
                  label={t("Cancel")}
                  isSubmit={false}
                  onClick={() => setConfirming(false)}
                  disabled={bulkRemove.isLoading}
                  classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-50"
                />
                <ButtonInput
                  label={t("Confirm Delete")}
                  isSubmit={false}
                  onClick={handleDelete}
                  isLoading={bulkRemove.isLoading}
                  disabled={bulkRemove.isLoading}
                  classes="!bg-red-600 hover:!bg-red-700"
                />
              </>
            ) : (
              <>
                <ButtonInput
                  label={t("Close")}
                  isSubmit={false}
                  onClick={() => setOpen(false)}
                  classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-50"
                />
                <ButtonInput
                  label={t("Delete Selected")}
                  isSubmit={false}
                  onClick={() => setConfirming(true)}
                  disabled={selectedIds.size === 0}
                  classes="!bg-red-600 hover:!bg-red-700"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
